import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const ACHIEVEMENT_IMAGES = [
  "https://readdy.ai/api/search-image?query=Japanese%20e-commerce%20online%20shopping%20concept%2C%20laptop%20showing%20online%20store%20with%20products%2C%20packages%20and%20delivery%20boxes%2C%20modern%20home%20office%20setting%2C%20clean%20minimal%20background%2C%20professional%20product%20photography%2C%20warm%20natural%20lighting&width=800&height=500&seq=ach001&orientation=landscape",
  "https://readdy.ai/api/search-image?query=web%20development%20and%20website%20design%20team%20working%20on%20computer%20screens%2C%20modern%20UI%20design%20mockups%20displayed%2C%20creative%20digital%20agency%20office%2C%20professional%20developers%20and%20designers%20collaborating%2C%20clean%20bright%20workspace%2C%20high%20resolution%20photography&width=800&height=500&seq=ach002&orientation=landscape",
];

export default function AchievementsSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-8");
          }
        });
      },
      { threshold: 0.15 }
    );
    const children = sectionRef.current?.querySelectorAll(".animate-on-scroll");
    children?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const achievements = [
    {
      image: ACHIEVEMENT_IMAGES[0],
      title: t("achievements.ec.title"),
      titleEn: t("achievements.ec.titleEn"),
      description: t("achievements.ec.description"),
    },
    {
      image: ACHIEVEMENT_IMAGES[1],
      title: t("achievements.web.title"),
      titleEn: t("achievements.web.titleEn"),
      description: t("achievements.web.description"),
    },
  ];

  return (
    <section id="achievements" className="py-20 md:py-28 bg-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 text-center mb-12 md:mb-16">
          <p className="text-xs text-gray-400 tracking-[0.4em] uppercase mb-3">
            {t("achievements.label")} / {t("achievements.labelJa")}
          </p>
          <h2 className="text-3xl md:text-4xl font-black text-[#1a2332] leading-tight mb-2">
            {t("achievements.title")}
          </h2>
          <p className="text-sm text-gray-400 tracking-widest">{t("achievements.titleEn")}</p>
        </div>

        {/* Achievement Cards with Photos */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {achievements.map((item, i) => (
            <div
              key={i}
              className={`animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 group rounded-2xl overflow-hidden bg-[#fafafa] hover:bg-white transition-colors`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              {/* Photo */}
              <div className="w-full h-56 md:h-64 overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              {/* Text */}
              <div className="p-7 md:p-8">
                <h3 className="text-xl font-bold text-[#1a2332] mb-1">{item.title}</h3>
                <p className="text-xs text-gray-400 tracking-wider mb-4">{item.titleEn}</p>
                <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
